import type { CompartmentInput } from '~/hooks/useMultiCompartmentInputs';
import { v4 as uuid } from 'uuid';
import type {
    StopCompartmentDetail,
    TaskCompartmentDetail,
    CompartmentType,
    CompartmentWithSize,
    CompartmentWithCapacity
} from '~/api/types/CommonTypes';
import type { ApiCompartmentTypes } from '~/api/types';

/**
 * Replaces special characters and white spaces for a given value
 */
export const replaceSpecialCharacters = (value: string) => {
    /**
     * Matches special characters and white spaces
     * @type {RexExp}
     */
    const specialCharactersRegexPattern = /([^\w]|_|\s)/g;
    const replacesWith = '-';

    return value.replace(specialCharactersRegexPattern, replacesWith);
};

/**
 * Initialize MultiCompartmentInput
 */
export const initializeCompartmentInput = () => {
    const firstComponentId = uuid();
    return {
        [firstComponentId]: {
            componentId: firstComponentId,
            selectedCompartmentId: '',
            capacity: '',
            label: '',
            unit: '',
            isFirst: true
        }
    };
};

/**
 * Format the compartments for the API
 */
const formatCompartmentList = (
    compartmentInputs: Record<string, CompartmentInput>
): CompartmentWithCapacity[] => {
    const compartmentInputValues = Object.values(compartmentInputs);

    const compartmentsRecord = compartmentInputValues.reduce(
        (
            compartmentList: Record<string, CompartmentWithCapacity>,
            compartmentInput: CompartmentInput
        ) => {
            const { selectedCompartmentId } = compartmentInput;

            if (!compartmentList[selectedCompartmentId]) {
                compartmentList[selectedCompartmentId] = {
                    compartmentType: compartmentInput.selectedCompartmentId,
                    capacity: Number(compartmentInput.capacity)
                };
            } else {
                compartmentList[selectedCompartmentId].capacity += Number(
                    compartmentInput.capacity
                );
            }

            return compartmentList;
        },
        {}
    );

    return Object.values(compartmentsRecord);
};

export const getVehicleCompartmentDetailList = (
    compartmentInputs: Record<string, CompartmentInput>
): CompartmentWithCapacity[] => {
    return formatCompartmentList(compartmentInputs);
};

export const getTaskCompartmentDetailList = (
    compartmentInputs: Record<string, CompartmentInput>
): CompartmentWithSize[] => {
    return formatCompartmentList(compartmentInputs).map((compartment) => {
        const { compartmentType, capacity: size } = compartment;
        return { compartmentType, size };
    });
};

export const parseCompartmentDetails = (
    sizeByCompartment: StopCompartmentDetail | TaskCompartmentDetail
): StopCompartmentDetail => {
    const { compartmentType } = sizeByCompartment as TaskCompartmentDetail;
    if (compartmentType) {
        const { id, label, unit } = compartmentType as CompartmentType;
        const { size } = sizeByCompartment;
        return { id, label, unit, size };
    }
    const { id, label, unit, size } =
        sizeByCompartment as StopCompartmentDetail;
    return { id, label, unit, size };
};

export const parseDefaultCompartmentDetails = (
    sizeByCompartment: (StopCompartmentDetail | TaskCompartmentDetail)[]
): StopCompartmentDetail | undefined => {
    const defaultCompartmentLabel = 'default';
    return sizeByCompartment
        .map(parseCompartmentDetails)
        .find(({ label }) => label === defaultCompartmentLabel);
};

/**
 * Map task's sizeByCompartment for display,
 * adds compartment details if not provided and replaces 'size' with 'capacity'
 * @param sizeByCompartment
 * @param compartmentTypesById
 */
export const mapSizeByCompartment = (
    sizeByCompartment: TaskCompartmentDetail[],
    compartmentTypesById: Record<string, ApiCompartmentTypes>
) => {
    return sizeByCompartment.map((compartment) => {
        const { compartmentType, size } = compartment;

        // when compartmentType contains ID only, get compartment details
        if (typeof compartmentType === 'string') {
            return {
                ...compartmentTypesById[compartmentType],
                capacity: size
            };
        }

        // when compartmentType contains compartment object
        return { ...compartmentType, capacity: size };
    });
};
